import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { collectionQueryOptions } from '@/features/collections';
import { CollectionPicker } from '@/features/resource-pickers';

import { MAX_DISCOUNT_COLLECTIONS } from '../schemas/discount-rules';

import { PickerField } from './picker-field';

function CollectionChip({
  storeId,
  id,
  title,
}: {
  storeId: string;
  id: string;
  title: string | undefined;
}) {
  const { data } = useQuery({
    ...collectionQueryOptions(storeId, id),
    enabled: title === undefined,
  });
  return <span className="max-w-60 truncate">{title ?? data?.title ?? id}</span>;
}

export interface AppliesToCollectionsFieldProps {
  storeId: string;
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  id?: string;
  'aria-invalid'?: boolean;
}

/** Collections of the discount's store (up to 50), picked in the shared collection picker. */
export function AppliesToCollectionsField({
  storeId,
  value,
  onChange,
  ...props
}: AppliesToCollectionsFieldProps) {
  const { t } = useTranslation('discounts');
  const [titles, setTitles] = useState<ReadonlyMap<string, string>>(new Map());

  return (
    <PickerField
      {...props}
      value={value}
      onChange={onChange}
      renderChip={(id) => <CollectionChip storeId={storeId} id={id} title={titles.get(id)} />}
      renderPicker={(picker) => (
        <CollectionPicker
          {...picker}
          storeId={storeId}
          multiple={MAX_DISCOUNT_COLLECTIONS}
          initialSelectedIds={value}
          onConfirm={(selection) => {
            setTitles((previous) => {
              const next = new Map(previous);
              for (const item of selection.items) next.set(item.id, item.title);
              return next;
            });
            onChange(selection.ids);
          }}
        />
      )}
      searchPlaceholder={t('appliesTo.searchCollections')}
      browseLabel={t('appliesTo.browse')}
      listLabel={t('appliesTo.selectedCollections')}
      removeLabel={() => t('appliesTo.remove')}
    />
  );
}
